import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from 'react-router-dom';
import { Container } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import DataGridV2 from "../../../components/DataGridV2";
import { apps, LOCAL_STORAGE_GRID_KEYS, } from "../../../_constants";
import { formatDate, getLocalStorageItem, setLocalStorageItem } from "../../../_helpers";
import { LabelColumnV2, LinkColumnV2 } from "../../../components/DataGridV2/DataColumnsV2";
import { getGridContextMenuList } from "../../../_helpers/getGridContextMenuList";
import { getCommunicationDetails, getContacts } from "../../../apis/communicationApi";
import { getContactAddressees, getContactGridNavigationItem } from "../util";
import { breadcrumbAction, tabNavigationAction } from "../../../_actions";
import { formatEmailLogStats } from "../../../_helpers/formatEmailLogStatsHelper";
import { INITIAL_ROW_SELECTION_MODEL } from "../../../components/DataGridV2/constants";

const ContactsGrid = () => {
  const classes = useStyles();
  const dispatch = useDispatch()
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const app = useSelector((state) => state.app);
  const { communicationId } = useParams();
  const { all = [], loading: buildingLoading } = useSelector(
    (state) => state.buildings
  );
  const apiRef = useGridApiRef();
  const keyPrefix = app === apps.aftercare ? "AFTERCARE" :  app === apps.huisinfo ? "BUYERS_GUIDE" : "GENERAL";
  const [rowsData, setRowsData] = useState([]);
  const [isRowsDataLoading, setIsRowsDataLoading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
    LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_PAGINATION[keyPrefix], undefined
  ));
  const [communicationDetails, setCommunicationDetails] = useState({})

  useEffect(() => {
    (async () => {
      setIsRowsDataLoading(true);
      try {
        const response = await getCommunicationDetails(communicationId);
        if (response.status === 200) {
          setCommunicationDetails(response.data)
        }
        setIsRowsDataLoading(false)
      } catch (err) {
        console.log(err)
        setIsRowsDataLoading(false);
      }
    })();
  }, [communicationId]);

  useEffect(() => {
    if (!isRowsDataLoading) {
      const contactGridNavigation = getContactGridNavigationItem({ t, communicationId, communicationDetails })
      dispatch(tabNavigationAction.setNavigationTab(contactGridNavigation.tabs, contactGridNavigation.tabs.findIndex((tab) => tab.to === location.pathname)))
      dispatch(breadcrumbAction.setBreadcrumbItem(contactGridNavigation.breadCrumbs))
    }
  }, [communicationDetails, communicationId, dispatch, isRowsDataLoading, location.pathname, t])

  useEffect(() => {
    setIsRowsDataLoading(true)
    if (!buildingLoading) {
      (async () => {
        try {
          const allContacts = (await getContacts(communicationId)).data ?? [];
          setRowsData(allContacts)
          setIsRowsDataLoading(false)
        } catch (err) {
          console.error(err);
          setIsRowsDataLoading(false)
        }
      })();
    }
    return () => {
      setRowsData([]);
    };
  }, [all, app, buildingLoading]);

  const getOptions = getGridContextMenuList({
    filterKey: LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_COLUMN_FILTER[keyPrefix],
    sortKey: LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_COLUMN_SORT[keyPrefix],
    i18n,
    t,
    apiRef,
    mode: "client",
    getValueForFilter: (rowData, extraData) => {
      let value = ""
      switch (extraData.fieldName) {
        case "event":
        case "totalAcceptedEmailLogs":
        case "totalDeliveredEmailLogs":
        case "totalOpenedEmailLogs":
        case "totalFailedEmailLogs":
          value = rowData[extraData.fieldName]?.toString()
          break;
        default:
          value = rowData[extraData.fieldName]
          break;
      }
      return value
    },
    getValueForCopy: (rowData, extraData) => {
      let text = ""
      switch (extraData.fieldName) {
        case "addressee":
          text = t(`communication.contact.addressee.${rowData.addressee}`)
          break;
        case "emailSentOn":
          text = rowData.emailSentOn ? formatDate(new Date(rowData.emailSentOn), true, "-", false) : "-"
          break;
        case "totalAcceptedEmailLogs":
        case "totalDeliveredEmailLogs":
        case "totalOpenedEmailLogs":
        case "totalFailedEmailLogs":
          text = formatEmailLogStats(rowData[extraData.fieldName], rowData["totalRecipients"])
          break;
        default:
          text = rowData[extraData.fieldName]
          break;
      }
      return text
    }
  })

  const onPaginationModelChange = useCallback((newPaginationModel) => {
    setPaginationModel(newPaginationModel)
    setLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_PAGINATION[keyPrefix],
      newPaginationModel
    );
  }, [keyPrefix])

  const columns = useMemo(() => {
    return [
      {
        field: "addressee",
        download: true,
        headerName: t('general.contact'),
        flex: 1,
        minWidth: 90,
        type: "singleSelect",
        valueOptions: getContactAddressees(t),
        getApplyQuickFilterFn:(searchVal)=>{
          return (value) => {
              return t(`communication.contact.addressee.${value}`)?.toLowerCase().includes(searchVal.toLowerCase())
          };
      },
        renderCell: (params) => {
          const rowData = params.row;
          if (!rowData) return "";
          return (<LabelColumnV2 label={t(`communication.contact.addressee.${rowData.addressee}`)}
            cellSubMenuOptions={{ options: getOptions({ field: "addressee", isQuickFilterDisplay: Boolean(rowData.addressee) }), extraData: { fieldName: "addressee", type: "singleSelect" } }}
            request={rowData}
          />
          )
        },
      },
      {
        field: "name",
        download: true,
        headerName: t('general.name'),
        flex: 1,
        minWidth: 90,
        renderCell: (params) => {
          const rowData = params.row;
          if (!rowData) return "";
          return (
            <LinkColumnV2 to={app === apps.huisinfo ? `/communicatie/${communicationId}/contact/` + params?.row?.id : `/nazorg/communicatie/${communicationId}/contact/` + params?.row?.id} label={rowData?.name ?? "-"}
              cellSubMenuOptions={{ options: getOptions({ field: "name", isQuickFilterDisplay: Boolean(rowData.name) }), extraData: { fieldName: "name", } }}
              request={rowData}
            />
          )
        },
      },
      {
        field: "emailSentOn",
        headerName: t('communication.contact.emailSent'),
        flex: 1,
        minWidth: 90,
        type: "dateTime",
        download: true,
        getApplyQuickFilterFn:(searchVal)=>{
          return (value) => {
              return formatDate(new Date(value), true, "-", false)?.toLowerCase().includes(searchVal.toLowerCase()) 
          };
      },
        valueGetter: (params) => {
          if (!params) return "";
          return new Date(params);
        },
        renderCell: (params) => {
          const rowData = params?.row;
          return <LabelColumnV2 label={rowData?.emailSentOn ? formatDate(new Date(rowData?.emailSentOn), true, "-", false) : "-"}
            cellSubMenuOptions={{ options: getOptions({ field: "emailSentOn", isQuickFilterDisplay: rowData?.emailSentOn }), extraData: { fieldName: "emailSentOn", type: "dateTime" } }}
            request={rowData}
          />
        },
      },
      {
        field: "totalAcceptedEmailLogs",
        download: true,
        headerName: t("communication.accepted"),
        flex: 1,
        minWidth: 90,
        align: "start",
        headerAlign: "start",
        type: 'number',
        renderCell: (params) => {
          const rowData = params?.row;
          if (!rowData) return "";
          const totalAcceptedEmailLogs = rowData?.totalAcceptedEmailLogs?.toString() || "0"
          const totalRecipients = rowData?.totalRecipients || 0
          return (
            <LabelColumnV2
              label={formatEmailLogStats(totalAcceptedEmailLogs, totalRecipients)}
              request={rowData}
              cellSubMenuOptions={{
                options: getOptions({ field: "totalAcceptedEmailLogs", isQuickFilterDisplay: totalRecipients > 0 }),
                extraData: { fieldName: "totalAcceptedEmailLogs", type: 'number' }
              }}
            />
          )
        },
      },
      {
        field: "totalDeliveredEmailLogs",
        download: true,
        headerName: t("communication.delivered"),
        flex: 1,
        minWidth: 90,
        align: "start",
        headerAlign: "start",
        type: 'number',
        renderCell: (params) => {
          const rowData = params?.row;
          if (!rowData) return "";
          const totalDeliveredEmailLogs = rowData?.totalDeliveredEmailLogs?.toString() || "0"
          const totalRecipients = rowData?.totalRecipients || 0
          return (
            <LabelColumnV2
            label={formatEmailLogStats(totalDeliveredEmailLogs, totalRecipients)}
              request={rowData}
              cellSubMenuOptions={{
                options: getOptions({ field: "totalDeliveredEmailLogs", isQuickFilterDisplay: totalRecipients > 0 }),
                extraData: { fieldName: "totalDeliveredEmailLogs", type: 'number' }
              }}
            />
          )
        },
      },
      {
        field: "totalOpenedEmailLogs",
        download: true,
        headerName: t("communication.opened"),
        flex: 1,
        minWidth: 90,
        align: "start",
        headerAlign: "start",
        type: 'number',
        renderCell: (params) => {
          const rowData = params?.row;
          if (!rowData) return "";
          const totalOpenedEmailLogs = rowData?.totalOpenedEmailLogs?.toString() || "0"
          const totalRecipients = rowData?.totalRecipients || 0
          return (
            <LabelColumnV2
              label={formatEmailLogStats(totalOpenedEmailLogs, totalRecipients)}
              request={rowData}
              cellSubMenuOptions={{
                options: getOptions({ field: "totalOpenedEmailLogs", isQuickFilterDisplay: totalRecipients > 0 }),
                extraData: { fieldName: "totalOpenedEmailLogs", type: 'number' }
              }}
            />
          )
        },
      },
      {
        field: "totalFailedEmailLogs",
        download: true,
        headerName: t("general.failed"),
        flex: 1,
        minWidth: 90,
        align: "start",
        headerAlign: "start",
        type: 'number',
        renderCell: (params) => {
          const rowData = params?.row;
          if (!rowData) return "";
          const totalFailedEmailLogs = rowData?.totalFailedEmailLogs?.toString() || "0"
          const totalRecipients = rowData?.totalRecipients || 0
          return (
            <LabelColumnV2
              label={formatEmailLogStats(totalFailedEmailLogs, totalRecipients)}
              request={rowData}
              cellSubMenuOptions={{
                options: getOptions({ field: "totalFailedEmailLogs", isQuickFilterDisplay: totalRecipients > 0 }),
                extraData: { fieldName: "totalFailedEmailLogs", type: 'number', }
              }}
            />
          )
        }
      }
    ];
  }, [getOptions, t]);

   const getContactsForPrintAndDownload = ({ isAllSelected = true ,idsToExport=[]}) => {
    const userSelectedRows = isAllSelected ? rowsData : idsToExport?.map((id) => rowsData.find(item => item?.id === id));
    const results = userSelectedRows?.map((item) => {
      const { totalRecipients, totalAcceptedEmailLogs, totalDeliveredEmailLogs, totalOpenedEmailLogs, totalFailedEmailLogs, emailSentOn, ...rest } = item || {};

      const emailSentOnFormatted = emailSentOn ? formatDate(new Date(emailSentOn), true, "-", false) : "-";
      const addressee = item?.["addressee"] ? t(`communication.contact.addressee.${item["addressee"]}`) : "-";
      return {
        ...rest,
        addressee,
        emailSentOn: emailSentOnFormatted,
        totalAcceptedEmailLogs: formatEmailLogStats(totalAcceptedEmailLogs, totalRecipients, true),
        totalDeliveredEmailLogs: formatEmailLogStats(totalDeliveredEmailLogs, totalRecipients, true),
        totalOpenedEmailLogs: formatEmailLogStats(totalOpenedEmailLogs, totalRecipients, true),
        totalFailedEmailLogs: formatEmailLogStats(totalFailedEmailLogs, totalRecipients, true)
      };
    });
  
    return results;
  };

  return (
    <Container maxWidth={false} className={classes.mainContainer}>
      <DataGridV2
        localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_SEARCH_TEXT[keyPrefix]}
        localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_COLUMN_FILTER[keyPrefix]}
        localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_COLUMN_SORT[keyPrefix]}
        localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_COLUMN_ORDER[keyPrefix]}
        localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_COLUMN_WIDTH[keyPrefix]}
        localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.CONTACTS_COMMUNICATION_COLUMN_VISIBILITY[keyPrefix]}
        apiRef={apiRef}
        loading={buildingLoading || isRowsDataLoading}
        checkboxSelection
        rows={rowsData}
        columns={columns}
        pagination
        {...(!!paginationModel && { paginationModel })}
        onPaginationModelChange={onPaginationModelChange}
        toolbarProps={{
          title: t("general.contact"),
          selectedRows: rowSelectionModel,
          getData: getContactsForPrintAndDownload,
        }}
        filterMode="client"
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
      />
    </Container>
  );
};
const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "auto",
    padding: 0,
  },
}));
export default ContactsGrid;